import { Modal, TableRow } from "@mui/material";
import { useEffect, useState } from "react";

import { Product } from "../../../../../../data/entities";
import CustomTable from "../../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../../../shared/components/IconButton";
import SearchBarWithSuggestion from "../../../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../../../../../shared/components/TextBox";
import { api } from "../../../../../../shared/hooks/api";
import { StyledNavLink } from "../../../../../../shared/styled/TableStyles";
import { StyledButton } from "../../../../../../shared/styled/TitleBar";
import DeleteIcon from "../../../../../../shared/svg/DeleteIcon";
import {
    formatInternationalNumber,
    parseInternationalToNumber,
} from "../../../../../../utils/format";
import { error } from "../../../../../../utils/toast";
import { withDefaultCustomerPrice } from "../../../../../../utils/customerProductPricing";
import {
    ButtonRow,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
    TitleWrapper,
} from "../../../../../ProductOrder/single/AddProductModal/Styled";

const ProductTableColumns = [
    { text: "Mã sản phẩm", icon: <></> },
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Giá khách hàng", icon: <></> },
    { text: "", icon: <></> },
];

interface ProductRowProps {
    item: Product & { price?: number };
    extraRowProps: {
        removeItem: (item: Product) => void;
        updatePrice: (item: Product, price: number) => void;
    };
}

const ProductRow = ({ item, extraRowProps }: ProductRowProps) => (
    <TableRow>
        <StyledTableCell align="left">
            <StyledNavLink to={`/products/${item.id}`}>
                {item.product_code || item.id}
            </StyledNavLink>
        </StyledTableCell>
        <StyledTableCell align="left">{item.name}</StyledTableCell>
        <StyledTableCell align="left">
            <TextBox
                numeric
                text={formatInternationalNumber(item.price ?? "")}
                onChangeText={(value: string) => {
                    const price = parseInternationalToNumber(value, error);
                    if (Number.isFinite(price)) {
                        extraRowProps.updatePrice(item, price);
                    }
                }}
            />
        </StyledTableCell>
        <StyledTableCell align="left">
            <IconButton onClick={() => extraRowProps.removeItem(item)}>
                <DeleteIcon />
            </IconButton>
        </StyledTableCell>
    </TableRow>
);

interface AddProductModalProps {
    open: boolean;
    handleClose: () => void;
    currentProducts: Product[];
    onProductsUpdated: (products: Product[]) => void;
}

const AddProductModal = ({
    open,
    handleClose,
    currentProducts,
    onProductsUpdated,
}: AddProductModalProps) => {
    const [{ data }, fetchProducts] = api.get(
        `/products?page_size=${Number.MAX_SAFE_INTEGER}`,
        undefined,
        true,
    );
    const [selectedItems, setSelectedItems] = useState<Product[]>(currentProducts);
    const products = data?.results ?? [];

    useEffect(() => {
        if (open) {
            fetchProducts({ search: "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSelectProduct = (product: Product, isAdded: boolean) => {
        const existing = selectedItems.some((item) => item.id === product.id);
        if (isAdded && !existing) {
            setSelectedItems([
                ...selectedItems,
                withDefaultCustomerPrice(product),
            ]);
        } else if (!isAdded && existing) {
            setSelectedItems(selectedItems.filter((item) => item.id !== product.id));
        }
    };

    const updatePrice = (target: Product, price: number) => {
        setSelectedItems((current) =>
            current.map((item) => item.id === target.id ? { ...item, price } : item),
        );
    };

    const removeItem = (target: Product) => {
        setSelectedItems((current) =>
            current.filter((item) => item.id !== target.id),
        );
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <ModalWrapper isOpen={open}>
                <div style={{ padding: "24px", minHeight: "500px" }}>
                    <TitleWrapper>
                        <TitleText>Thêm sản phẩm</TitleText>
                        <InfoText>
                            Chọn sản phẩm hiện có và điều chỉnh giá cho khách hàng
                        </InfoText>
                    </TitleWrapper>
                    <SearchBarWithSuggestion
                        menuList={products}
                        selectedItems={selectedItems}
                        fetchItems={(variables: any) => fetchProducts(variables)}
                        handleOnclickItem={handleSelectProduct}
                        placeholder="Tìm kiếm theo mã hoặc tên sản phẩm"
                    />
                    <CustomTable
                        itemList={selectedItems}
                        RowElement={ProductRow}
                        TableColumns={ProductTableColumns}
                        extraRowProps={{ removeItem, updatePrice }}
                        containerStyle={{ maxHeight: "400px" }}
                    />
                    <ButtonRow>
                        <StyledButton onClick={handleClose}>Huỷ bỏ</StyledButton>
                        <StyledButton
                            variant="contained"
                            onClick={() => {
                                onProductsUpdated(selectedItems);
                                handleClose();
                            }}
                        >
                            Lưu sản phẩm
                        </StyledButton>
                    </ButtonRow>
                </div>
            </ModalWrapper>
        </Modal>
    );
};

export default AddProductModal;
